import { useTranslation } from 'react-i18next'
import { Aurora, AuroraAssetGroup } from '../types'
import { useState } from 'react'
import { setAuroraHeatPumpStatus } from '../services/aurora'
import { AuroraHeatPumpStatus } from '../enums'
import { useDispatch } from 'react-redux'
import AuroraHeatPumpStatusControl from './AuroraHeatPumpStatusControl'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'

export interface AuroraAssetGroupHeatPumpStatusControlProps {
  auroraAssetGroup: AuroraAssetGroup
  beforeSubmit?: (status: AuroraHeatPumpStatus) => void
  afterSubmit?: (status: AuroraHeatPumpStatus) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupHeatPumpStatusControl({
  auroraAssetGroup,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupHeatPumpStatusControlProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The auroras.
   */
  const auroras = auroraAssetGroup.assets
    .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
    .map(({ aurora, ...asset }) => ({ ...aurora, asset }) as Aurora)

  /**
   * The heat pump status.
   */
  const [status, setStatus] = useState<AuroraHeatPumpStatus>(
    auroras.every((aurora) => aurora.isHeatPumpOn)
      ? AuroraHeatPumpStatus.ON
      : AuroraHeatPumpStatus.OFF
  )

  /**
   * The submitting state.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * Handle the submit event.
   */
  async function handleSubmit(status: AuroraHeatPumpStatus) {
    try {
      setSubmitting(true)

      beforeSubmit?.(status)

      await Promise.all(auroras.map((aurora) => setAuroraHeatPumpStatus(aurora.id, status)))
      setStatus(status)
      dispatch(createSuccessNotification(t('setHeatPumpStatusSuccess')))

      afterSubmit?.(status)
    } catch (error) {
      onError?.(error)
      dispatch(createErrorNotification(t('setHeatPumpStatusError')))
    } finally {
      setSubmitting(false)
    }
  }

  if (auroras.length === 0) {
    return null
  }

  return (
    <AuroraHeatPumpStatusControl value={status} loading={isSubmitting} onSubmit={handleSubmit} />
  )
}
